import { cn } from "@duck/ui"
import { WorkspaceTab, WorkspaceTabOverlay, getTabClassName } from "./WorkspaceTab"
import { AddWorkspaceTabTab, type AddWorkspaceTabTabRef } from "./AddWorkspaceTabTab"
import { useTabStyle } from "./tabStyle"
import { workspaceTabsDefinitions } from "./workspaceTabs.definitions"
import { ChallengeService } from "@duck/api-definitions"
import { useState, useRef, forwardRef, useImperativeHandle, memo, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from "@dnd-kit/core"
import {
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
} from "@dnd-kit/sortable"
import { restrictToHorizontalAxis, restrictToParentElement } from "@dnd-kit/modifiers"
import { TranslationKey, useTar } from "@/modules/i18n"
import { ArrowDownToLine } from "lucide-react"
import { trackWorkspace } from '@/modules/workspaceAnalytics'
import { useAtomValue } from 'jotai'
import { TabIndexHint } from '@/modules/Shortcuts'
import { workspaceChallengeIdAtom } from '@/store/workspaceAtoms'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { FONT_SIZE_NORMAL, useFontSize } from '@/hooks/useFontSize'
import { ConfirmTabCloseDialog } from './ConfirmTabCloseDialog'

interface Props {
    initialTabsState: {
        tabs: ChallengeService.WorkspaceTab[]
        activeTabIndex: number
    }
    defaultQuery?: string
    /** Move existing tab to split/other view; called with (tab, tabIndexInView). */
    onMoveTabToSplitView?: (tab: ChallengeService.WorkspaceTab, tabIndexInView: number) => void
    /** Add a new tab in split view (1 view) or in the other view (2 views); from + menu. */
    onAddTabInSplitView?: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    /** Tooltip for + menu "open in split/other view": 'common.openInSplitView' or 'common.openInOtherView' */
    addTabInSplitViewTooltipKey?: TranslationKey
    onCloseView?: () => void
    /** Structural change: add/remove/reorder tabs, switch active tab. Parent updates state and rerenders. */
    onTabsChange?: (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => void
    /** Content-only change: tab data updated (e.g. SQL typing). Parent persists without rerender. */
    onTabsDataChange?: (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => void
    /** Whether this view is part of a split layout (affects drag modifiers to allow cross-view drag). */
    isSplitView?: boolean
    /** Whether a sibling view is currently dragging a tab (shows drop zone indicator). */
    siblingIsDragging?: boolean
    /** Tab type being dragged in the sibling view (for ghost tab preview). */
    siblingDraggedTabType?: ChallengeService.WorkspaceTabTypeEnum
    /** Called when drag state changes in this view. */
    onDragActiveChange?: (active: boolean, tabType?: ChallengeService.WorkspaceTabTypeEnum) => void
    /** Called when a tab is dragged outside all sortables (for cross-view drop). */
    onDragOutside?: (tab: ChallengeService.WorkspaceTab, tabIndex: number, pointerPosition: { x: number; y: number }) => void
    /** Callback to register/unregister a portal target for singleton tabs. Called with the DOM element when a portalSingleton tab is active/visible, null otherwise. */
    onPortalTargetChange?: (tabType: ChallengeService.WorkspaceTabTypeEnum, el: HTMLElement | null) => void
    /** Set of tab types currently open across all views (for onlyOne enforcement in the add-tab dropdown). */
    openTabTypes?: Set<ChallengeService.WorkspaceTabTypeEnum>
    /** When true, the add-tab button shows the Alt+'+' ShortcutHint while Alt is held. Parent sets this only on the focused view in split mode. */
    showAddTabShortcutHint?: boolean
    /** Preview mode: used by settings dialog to demo tab styling. Suppresses analytics, replaces content pane with an inert placeholder, and hides the add-tab button. */
    previewMode?: boolean
    /** Offset added to the per-tab TabIndexHint number so split-view enumeration continues across views (e.g. view 1 starts at view 0's tab count + 1). */
    indexOffset?: number
}

export interface WorkspaceTabsViewRef {
    addTab: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    switchToTab: (index: number) => void
    closeActiveTab: (method: 'keyboard' | 'button' | 'middle_click') => void
    openAddTabMenu: () => void
}

let instanceIdCounter = 0

const createInitialState = (initial: Props['initialTabsState']) => {
    const tabs = initial.tabs.map(tab => ({
        tab,
        instanceId: `tab-${instanceIdCounter++}`,
    }))
    const safeIndex = tabs.length
        ? Math.min(Math.max(initial.activeTabIndex, 0), tabs.length - 1)
        : 0
    return { tabs, activeInstanceId: tabs[safeIndex]?.instanceId ?? tabs[0]?.instanceId ?? '' }
}

/** Memoized so tab content does not re-render on drag start/end or other parent state (e.g. draggedIndex). */
const WorkspaceTabContent = memo(
    ({
        tab,
        onUpdateTabData,
        isVisible,
    }: {
        tab: ChallengeService.WorkspaceTab
        onUpdateTabData: (data: string) => void
        isVisible: boolean
    }) => {
        const Component = workspaceTabsDefinitions[tab.type]?.Component
        return Component ? <Component data={tab.data ?? ""} onUpdateData={onUpdateTabData} isVisible={isVisible} /> : null
    },
)
WorkspaceTabContent.displayName = 'WorkspaceTabContent'

export const WorkspaceTabsView = forwardRef<WorkspaceTabsViewRef, Props>(({ initialTabsState, defaultQuery = '', onMoveTabToSplitView, onAddTabInSplitView, addTabInSplitViewTooltipKey, onCloseView, onTabsChange, onTabsDataChange, isSplitView, siblingIsDragging, siblingDraggedTabType, onDragActiveChange, onDragOutside, onPortalTargetChange, openTabTypes, showAddTabShortcutHint, previewMode, indexOffset = 0 }, ref) => {
    const { t } = useTar()
    const challengeId = useAtomValue(workspaceChallengeIdAtom)
    const tabStyle = useTabStyle()
    const { getSetting, setSetting } = useLocalSettings()
    const confirmTabClose = getSetting('confirmTabClose') ?? false
    const fontSize = useFontSize()
    const contentZoom = previewMode ? 1 : fontSize / FONT_SIZE_NORMAL
    const [pendingClose, setPendingClose] = useState<{
        instanceId: string
        tabType: ChallengeService.WorkspaceTabTypeEnum
        method: 'button' | 'middle_click' | 'keyboard'
    } | null>(null)
    const [{ tabs, activeInstanceId }, setTabsState] = useState(() => createInitialState(initialTabsState))
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
    const activeIdRef = useRef<string | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const tabRefsMap = useRef<Record<string, HTMLDivElement | null>>({})
    /** Latest tab data by instanceId (content-only updates, no rerender). Merged when building contract for structural changes. */
    const tabDataRef = useRef<Record<string, string>>({})
    /** Track real pointer position during drag (dnd-kit delta is clamped by modifiers). */
    const pointerPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
    const pointerMoveHandlerRef = useRef<((e: PointerEvent) => void) | null>(null)
    const tabBarRef = useRef<HTMLDivElement>(null)
    const [isPointerOverTabBar, setIsPointerOverTabBar] = useState(false)
    const splitDropZoneRef = useRef<HTMLDivElement>(null)
    const [isPointerOverSplitZone, setIsPointerOverSplitZone] = useState(false)
    const portalPlaceholderRefs = useRef<Record<string, HTMLDivElement | null>>({})
    const addTabRef = useRef<AddWorkspaceTabTabRef>(null)

    const showSplitDropZone = draggedIndex !== null && !isSplitView && !!onMoveTabToSplitView
    const isPointerOverTabBarRef = useRef(false)
    const isPointerOverSplitZoneRef = useRef(false)

    // Single consolidated pointermove listener for hover tracking (sibling drag + split drop zone)
    useEffect(() => {
        const needsTabBarTracking = siblingIsDragging
        const needsSplitZoneTracking = showSplitDropZone
        if (!needsTabBarTracking && !needsSplitZoneTracking) {
            setIsPointerOverTabBar(false)
            setIsPointerOverSplitZone(false)
            return
        }
        const handler = (e: PointerEvent) => {
            if (needsTabBarTracking && tabBarRef.current) {
                const rect = tabBarRef.current.getBoundingClientRect()
                const over = e.clientX >= rect.left && e.clientX <= rect.right &&
                    e.clientY >= rect.top && e.clientY <= rect.bottom
                if (over !== isPointerOverTabBarRef.current) {
                    isPointerOverTabBarRef.current = over
                    setIsPointerOverTabBar(over)
                }
            }
            if (needsSplitZoneTracking && splitDropZoneRef.current) {
                const rect = splitDropZoneRef.current.getBoundingClientRect()
                const over = e.clientX >= rect.left && e.clientX <= rect.right &&
                    e.clientY >= rect.top && e.clientY <= rect.bottom
                if (over !== isPointerOverSplitZoneRef.current) {
                    isPointerOverSplitZoneRef.current = over
                    setIsPointerOverSplitZone(over)
                }
            }
        }
        document.addEventListener('pointermove', handler)
        return () => {
            document.removeEventListener('pointermove', handler)
            isPointerOverTabBarRef.current = false
            isPointerOverSplitZoneRef.current = false
        }
    }, [siblingIsDragging, showSplitDropZone])

    useEffect(() => {
        if (!onPortalTargetChange) return
        const portalTypes: ChallengeService.WorkspaceTabTypeEnum[] = []
        for (const { instanceId, tab } of tabs) {
            const def = workspaceTabsDefinitions[tab.type]
            if (!def?.portalSingleton) continue
            portalTypes.push(tab.type)
            const isActive = instanceId === activeInstanceId
            const el = isActive ? portalPlaceholderRefs.current[instanceId] ?? null : null
            onPortalTargetChange(tab.type, el)
        }
        return () => {
            for (const type of portalTypes) {
                onPortalTargetChange(type, null)
            }
        }
    }, [activeInstanceId, tabs, onPortalTargetChange])

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const cleanupPointerTracking = () => {
        if (pointerMoveHandlerRef.current) {
            document.removeEventListener('pointermove', pointerMoveHandlerRef.current)
            pointerMoveHandlerRef.current = null
        }
    }

    const handleDragStart = useCallback(({ active }: DragStartEvent) => {
        activeIdRef.current = activeInstanceId
        const idx = tabs.findIndex(t => t.instanceId === active.id)
        setDraggedIndex(idx)
        onDragActiveChange?.(true, idx !== -1 ? tabs[idx].tab.type : undefined)
        cleanupPointerTracking()
        const handler = (e: PointerEvent) => { pointerPosRef.current = { x: e.clientX, y: e.clientY } }
        pointerMoveHandlerRef.current = handler
        document.addEventListener('pointermove', handler)
    }, [tabs, activeInstanceId, onDragActiveChange])

    const handleDragEnd = useCallback(({ active, over }: DragEndEvent) => {
        let handledCrossView = false

        // Check if pointer ended over the split drop zone (single-view mode)
        if (!isSplitView && onMoveTabToSplitView && splitDropZoneRef.current) {
            const pointer = pointerPosRef.current
            const rect = splitDropZoneRef.current.getBoundingClientRect()
            if (pointer.x >= rect.left && pointer.x <= rect.right && pointer.y >= rect.top && pointer.y <= rect.bottom) {
                const draggedIdx = tabs.findIndex(t => t.instanceId === active.id)
                if (draggedIdx !== -1) {
                    if (!previewMode) trackWorkspace('workspace:tab:move_to_split', { challengeId, tabType: tabs[draggedIdx].tab.type })
                    onMoveTabToSplitView(tabs[draggedIdx].tab, draggedIdx)
                    handledCrossView = true
                }
            }
        }

        // Check if real pointer ended outside this view — attempt cross-view move or split creation
        if (!handledCrossView && onDragOutside && containerRef.current) {
            const pointer = pointerPosRef.current
            const rect = containerRef.current.getBoundingClientRect()
            if (pointer.x < rect.left || pointer.x > rect.right || pointer.y < rect.top || pointer.y > rect.bottom) {
                const draggedIdx = tabs.findIndex(t => t.instanceId === active.id)
                if (draggedIdx !== -1) {
                    if (!previewMode) trackWorkspace('workspace:tab:move_to_split', { challengeId, tabType: tabs[draggedIdx].tab.type })
                    onDragOutside(tabs[draggedIdx].tab, draggedIdx, pointer)
                    handledCrossView = true
                }
            }
        }

        if (!handledCrossView && over && active.id !== over.id) {
            const oldIndex = tabs.findIndex(t => t.instanceId === active.id)
            const newIndex = tabs.findIndex(t => t.instanceId === over.id)
            const newTabs = [...tabs]
            const [removed] = newTabs.splice(oldIndex, 1)
            newTabs.splice(newIndex, 0, removed)
            if (!previewMode) trackWorkspace('workspace:tab:reorder', { challengeId, tabType: removed.tab.type })
            setTabsState({ tabs: newTabs, activeInstanceId })
            const newActiveTabIndex = newTabs.findIndex(t => t.instanceId === activeInstanceId)
            const contractTabs = newTabs.map(t => ({ type: t.tab.type, data: tabDataRef.current[t.instanceId] ?? t.tab.data }))
            onTabsChange?.(contractTabs, newActiveTabIndex)
        }
        setDraggedIndex(null)
        onDragActiveChange?.(false)
        cleanupPointerTracking()
        queueMicrotask(() => { activeIdRef.current = null })
    }, [tabs, activeInstanceId, isSplitView, onMoveTabToSplitView, onDragOutside, onTabsChange, onDragActiveChange, previewMode])

    const handleDragCancel = useCallback(() => {
        activeIdRef.current = null
        setDraggedIndex(null)
        onDragActiveChange?.(false)
        cleanupPointerTracking()
    }, [onDragActiveChange])

    const handleAddTab = (tabType: ChallengeService.WorkspaceTabTypeEnum) => {
        const newInstanceId = `tab-${instanceIdCounter++}`
        const initialData = tabType === ChallengeService.WorkspaceTabTypeEnum.Sql ? defaultQuery : undefined
        const newTabs = [
            ...tabs,
            {
                tab: {
                    type: tabType,
                    data: initialData,
                },
                instanceId: newInstanceId,
            },
        ]
        setTabsState({
            tabs: newTabs,
            activeInstanceId: newInstanceId,
        })
        const contractTabs = newTabs.map(t => ({ type: t.tab.type, data: tabDataRef.current[t.instanceId] ?? t.tab.data }))
        onTabsChange?.(contractTabs, newTabs.length - 1)
    }

    const switchToTab = (index: number) => {
        if (index < 0 || index >= tabs.length) return
        const target = tabs[index]
        setTabsState({ tabs, activeInstanceId: target.instanceId })
        const contractTabs = tabs.map((t) => ({
            type: t.tab.type,
            data: tabDataRef.current[t.instanceId] ?? t.tab.data,
        }))
        onTabsChange?.(contractTabs, index)
        tabRefsMap.current[target.instanceId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
        })
    }

    const closeActiveTab = (method: 'keyboard' | 'button' | 'middle_click') => {
        // Mirror the X-button's gating: only act if removing this tab is allowed
        // (more than one tab in this view, or we can collapse the view entirely).
        if (tabs.length <= 1 && !onCloseView) return
        const target = tabs.find(t => t.instanceId === activeInstanceId)
        if (!target) return
        if (previewMode) {
            handleRemoveTab(activeInstanceId)
            return
        }
        if (confirmTabClose) {
            setPendingClose({ instanceId: activeInstanceId, tabType: target.tab.type, method })
            return
        }
        trackWorkspace('workspace:tab:close', { challengeId, tabType: target.tab.type, method })
        handleRemoveTab(activeInstanceId)
    }

    useImperativeHandle(ref, () => ({
        addTab: handleAddTab,
        switchToTab,
        closeActiveTab,
        openAddTabMenu: () => addTabRef.current?.open(),
    }), [tabs, activeInstanceId, defaultQuery, onTabsChange, onCloseView, confirmTabClose, previewMode, challengeId])

    const handleRemoveTab = (instanceId: string) => {
        if (tabs.length <= 1) {
            // If it's the last tab and we can close the view, do so
            onCloseView?.()
            return
        }
        const removedIndex = tabs.findIndex(t => t.instanceId === instanceId)
        const newTabs = tabs.filter(t => t.instanceId !== instanceId)
        const newActiveInstanceId =
            instanceId === activeInstanceId ? newTabs[Math.max(0, removedIndex - 1)].instanceId : activeInstanceId
        const newActiveTabIndex = newTabs.findIndex(t => t.instanceId === newActiveInstanceId)
        setTabsState({ tabs: newTabs, activeInstanceId: newActiveInstanceId })
        delete tabDataRef.current[instanceId]
        const contractTabs = newTabs.map(t => ({ type: t.tab.type, data: tabDataRef.current[t.instanceId] ?? t.tab.data }))
        onTabsChange?.(contractTabs, newActiveTabIndex)
    }

    const draggedTab = draggedIndex !== null ? tabs[draggedIndex] : null

    const handleUpdateTabData = (instanceId: string, data: string) => {
        const index = tabs.findIndex(t => t.instanceId === instanceId)
        if (index === -1) return
        tabDataRef.current[instanceId] = data
        const newActiveIndex = tabs.findIndex(t => t.instanceId === activeInstanceId)
        const contractTabs = tabs.map(t => ({ type: t.tab.type, data: t.instanceId === instanceId ? data : (tabDataRef.current[t.instanceId] ?? t.tab.data) }))
            // Content-only: persist via parent ref, no local setState. SqlTab manages its own state; no rerender needed.
            ; (onTabsDataChange ?? onTabsChange)?.(contractTabs, newActiveIndex)
    }

    return (
      <div ref={containerRef} className="h-full min-h-0 w-full flex flex-col">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div
            ref={tabBarRef}
            className={cn([
              "min-w-0 overflow-x-auto overflow-y-hidden",
              "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] relative flex items-end",
              "bg-gradient-to-b from-background to-muted/10",
            ])}
          >
            {siblingIsDragging && (
              <div
                className={cn(
                  'absolute inset-0 z-[250] border-2 border-dashed rounded-lg cursor-grabbing transition-colors flex items-center justify-center',
                  isPointerOverTabBar
                    ? 'bg-primary/15 border-primary/50'
                    : 'bg-primary/5 border-primary/30'
                )}
              >
                <ArrowDownToLine className={cn(
                  'size-5 transition-colors',
                  isPointerOverTabBar ? 'text-primary/60' : 'text-primary/25'
                )} />
              </div>
            )}
            <div
              role="tablist"
              aria-orientation="horizontal"
              className={cn(
                'flex items-end w-fit shrink-0 pt-3',
                tabStyle === 'rounded' && 'ms-1 -space-x-2 rtl:space-x-reverse',
                tabStyle === 'boxes' && '-space-x-px rtl:space-x-reverse'
              )}
            >
              <SortableContext
                items={tabs.map((t) => t.instanceId)}
                strategy={horizontalListSortingStrategy}
              >
                {tabs.map((tab, index) => (
                  <TabIndexHint key={tab.instanceId} index={index + 1 + indexOffset}>
                    <WorkspaceTab
                      sortableId={tab.instanceId}
                      index={index}
                      label={workspaceTabsDefinitions[tab.tab.type]?.label}
                      Icon={workspaceTabsDefinitions[tab.tab.type]?.Icon}
                      iconColor={workspaceTabsDefinitions[tab.tab.type]?.iconColor}
                      isActive={
                        tab.instanceId ===
                        (activeIdRef.current ?? activeInstanceId)
                      }
                      isDragActive={draggedIndex !== null}
                      tabRef={(el) => {
                        tabRefsMap.current[tab.instanceId] = el
                      }}
                      onActivateTab={() => {
                        const newActiveIndex = tabs.findIndex(
                          (t) => t.instanceId === tab.instanceId
                        )
                        if (!previewMode) trackWorkspace('workspace:tab:switch', { challengeId, tabType: tab.tab.type })
                        setTabsState({ tabs, activeInstanceId: tab.instanceId })
                        const contractTabs = tabs.map((t) => ({
                          type: t.tab.type,
                          data: tabDataRef.current[t.instanceId] ?? t.tab.data,
                        }))
                        onTabsChange?.(contractTabs, newActiveIndex)
                        tabRefsMap.current[tab.instanceId]?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'nearest',
                          inline: 'nearest',
                        })
                      }}
                      onRemoveTab={
                        tabs.length > 1 || onCloseView
                          ? (method) => {
                              const resolvedMethod = method ?? 'button'
                              if (previewMode) {
                                handleRemoveTab(tab.instanceId)
                                return
                              }
                              if (confirmTabClose) {
                                setPendingClose({ instanceId: tab.instanceId, tabType: tab.tab.type, method: resolvedMethod })
                                return
                              }
                              trackWorkspace('workspace:tab:close', { challengeId, tabType: tab.tab.type, method: resolvedMethod })
                              handleRemoveTab(tab.instanceId)
                            }
                          : undefined
                      }
                      disabled={tabs.length <= 1 && !isSplitView}
                    />
                  </TabIndexHint>
                ))}
              </SortableContext>
              {siblingIsDragging &&
                siblingDraggedTabType &&
                isPointerOverTabBar && (() => {
                  const ghostDef = workspaceTabsDefinitions[siblingDraggedTabType]
                  const GhostIcon = ghostDef.Icon
                  return (
                    <div
                      className={cn(
                        getTabClassName(false, false, false, undefined, tabStyle),
                        'opacity-60 pointer-events-none'
                      )}
                    >
                      <span className="relative z-10 pt-1 flex items-center gap-1.5 whitespace-nowrap">
                        <GhostIcon className={cn('size-3.5 shrink-0', ghostDef.iconColor)} aria-hidden />
                        {t(ghostDef.label)}
                      </span>
                    </div>
                  )
                })()}
              {!previewMode && (
                <AddWorkspaceTabTab
                  ref={addTabRef}
                  onAddTab={handleAddTab}
                  onOpenInSplitView={onAddTabInSplitView}
                  openInSplitViewTooltipKey={addTabInSplitViewTooltipKey}
                  openTabTypes={openTabTypes}
                  showShortcutHint={showAddTabShortcutHint}
                />
              )}
            </div>
            {showSplitDropZone && (
              <div
                ref={splitDropZoneRef}
                className={cn(
                  'flex-1 mx-1 self-stretch border-2 border-dashed rounded-lg cursor-grabbing transition-colors flex items-center justify-center pointer-events-none',
                  isPointerOverSplitZone
                    ? 'bg-primary/15 border-primary/50'
                    : 'bg-primary/5 border-primary/30'
                )}
              >
                <ArrowDownToLine className={cn(
                  'size-4 transition-colors shrink-0',
                  isPointerOverSplitZone ? 'text-primary/60' : 'text-primary/25'
                )} />
                <span className={cn(
                  'text-sm whitespace-nowrap ms-1.5 transition-colors',
                  isPointerOverSplitZone ? 'text-primary/60' : 'text-primary/25'
                )}>
                  {t('common.dropHereForSplitView')}
                </span>
              </div>
            )}
          </div>
          {createPortal(
            <DragOverlay dropAnimation={null} style={{ zIndex: 9999 }}>
              {draggedTab && (
                <WorkspaceTabOverlay
                  label={workspaceTabsDefinitions[draggedTab.tab.type].label}
                  Icon={workspaceTabsDefinitions[draggedTab.tab.type].Icon}
                  iconColor={workspaceTabsDefinitions[draggedTab.tab.type].iconColor}
                  isActive={draggedTab.instanceId === activeInstanceId}
                />
              )}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
        <ConfirmTabCloseDialog
          open={pendingClose !== null}
          tabLabelKey={pendingClose ? workspaceTabsDefinitions[pendingClose.tabType]?.label ?? null : null}
          onCancel={() => setPendingClose(null)}
          onConfirm={(dontAskAgain) => {
            if (!pendingClose) return
            if (dontAskAgain) setSetting('confirmTabClose', false)
            trackWorkspace('workspace:tab:close', { challengeId, tabType: pendingClose.tabType, method: pendingClose.method })
            handleRemoveTab(pendingClose.instanceId)
            setPendingClose(null)
          }}
        />
        <div
          className="border-t border-border flex-1 min-w-0 min-h-0 flex flex-col"
          style={{ zoom: contentZoom }}
        >
          <div className="relative flex-1 min-h-0 min-w-0">
            {previewMode ? (
              <div className="absolute inset-0 bg-background" aria-hidden />
            ) : tabs.map(({ instanceId, tab }) => {
                const def = workspaceTabsDefinitions[tab.type]
                const isActive = instanceId === activeInstanceId
                return (
                    <div
                        key={instanceId}
                        className={cn(
                            'absolute inset-0 overflow-auto custom-scrollbar',
                            !isActive && 'invisible pointer-events-none',
                            isActive && 'z-[1]'
                        )}
                        aria-hidden={!isActive}
                    >
                        {def?.portalSingleton ? (
                            <div
                                ref={(el) => { portalPlaceholderRefs.current[instanceId] = el }}
                                className="h-full w-full"
                            />
                        ) : (
                            <WorkspaceTabContent
                                tab={tab}
                                onUpdateTabData={(data) => handleUpdateTabData(instanceId, data)}
                                isVisible={isActive}
                            />
                        )}
                    </div>
                )
            })}
          </div>
        </div>
      </div>
    )
})

WorkspaceTabsView.displayName = 'WorkspaceTabsView'